// ========================================
// ERROR REPORTING SERVICE
// Sentry-compatible interface for error monitoring
// Can be swapped with actual Sentry SDK when ready
// ========================================

import { FEATURE_FLAGS, APP_CONFIG } from '../config';

const IS_ENABLED = FEATURE_FLAGS.ERROR_REPORTING;
const IS_DEV = process.env.NODE_ENV !== 'production';

// Error severity levels
export const ErrorSeverity = {
  FATAL: 'fatal',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEBUG: 'debug',
};

// Error categories for filtering
export const ErrorCategory = {
  AUTHENTICATION: 'auth',
  NETWORK: 'network',
  VALIDATION: 'validation',
  UI: 'ui',
  API: 'api',
  UNKNOWN: 'unknown',
};

// In-memory error buffer for batching (when real service is connected)
const errorBuffer = [];
const MAX_BUFFER_SIZE = 100;

// User context for error reports
let userContext = null;
let sessionContext = {};

/**
 * Initialize error reporting service
 * @param {Object} options - Initialization options
 * @param {string} options.dsn - Service DSN (for Sentry, etc.)
 * @param {string} options.environment - Environment name
 * @param {string} options.release - App version/release
 */
export function initErrorReporting(options = {}) {
  if (!IS_ENABLED) {
    if (IS_DEV) {
      console.log('[ErrorReporting] Disabled in development mode');
    }
    return;
  }

  sessionContext = {
    environment: options.environment || process.env.NODE_ENV,
    release: options.release || APP_CONFIG.VERSION,
    startTime: new Date().toISOString(),
  };

  // TODO: Initialize actual error service (Sentry, etc.)
  // Example:
  // Sentry.init({
  //   dsn: options.dsn,
  //   environment: sessionContext.environment,
  //   release: sessionContext.release,
  //   tracesSampleRate: 0.1,
  // });

  if (IS_DEV) {
    console.log('[ErrorReporting] Initialized with context:', sessionContext);
  }

  // Set up global error handlers
  setupGlobalHandlers();
}

/**
 * Set user context for error reports
 * @param {Object|null} user - User object or null to clear
 */
export function setUserContext(user) {
  if (!user) {
    userContext = null;
    return;
  }

  // Only include non-sensitive user info
  userContext = {
    id: user.id || user.uid,
    email: user.email ? maskEmail(user.email) : undefined,
    role: user.role,
  };

  // TODO: Update actual error service
  // Sentry.setUser(userContext);
}

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 * @param {string} breadcrumb.category - Category (e.g., 'navigation', 'api', 'ui')
 * @param {string} breadcrumb.message - Description
 * @param {Object} breadcrumb.data - Additional data
 * @param {string} breadcrumb.level - Severity level
 */
export function addBreadcrumb(breadcrumb) {
  if (!IS_ENABLED) return;

  const crumb = {
    timestamp: new Date().toISOString(),
    category: breadcrumb.category || 'default',
    message: breadcrumb.message,
    data: breadcrumb.data,
    level: breadcrumb.level || ErrorSeverity.INFO,
  };

  // TODO: Add to actual error service
  // Sentry.addBreadcrumb(crumb);

  if (IS_DEV) {
    console.log('[Breadcrumb]', crumb);
  }
}

/**
 * Capture and report an error
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 * @param {string} context.category - Error category
 * @param {string} context.severity - Severity level
 * @param {Object} context.extra - Additional data
 * @param {string[]} context.tags - Tags for filtering
 */
export function captureError(error, context = {}) {
  const {
    category = ErrorCategory.UNKNOWN,
    severity = ErrorSeverity.ERROR,
    extra = {},
    tags = [],
  } = context;

  const errorReport = {
    timestamp: new Date().toISOString(),
    error: {
      name: error.name,
      message: error.message,
      stack: error.stack,
    },
    context: {
      category,
      severity,
      extra: sanitizeExtraData(extra),
      tags,
      user: userContext,
      session: sessionContext,
    },
  };

  // Always log to console in development
  if (IS_DEV) {
    console.error('[ErrorReporting] Captured error:', errorReport);
  }

  if (!IS_ENABLED) return;

  // Buffer errors for batch sending
  addToBuffer(errorReport);

  // TODO: Send to actual error service
  // Sentry.captureException(error, {
  //   tags: { category, ...Object.fromEntries(tags.map(t => [t, true])) },
  //   extra: sanitizeExtraData(extra),
  //   level: severity,
  // });
}

/**
 * Capture a message (non-error)
 * @param {string} message - Message to capture
 * @param {Object} context - Additional context
 */
export function captureMessage(message, context = {}) {
  const {
    category = ErrorCategory.UNKNOWN,
    severity = ErrorSeverity.INFO,
    extra = {},
    tags = [],
  } = context;

  const report = {
    timestamp: new Date().toISOString(),
    message,
    context: {
      category,
      severity,
      extra: sanitizeExtraData(extra),
      tags,
      user: userContext,
      session: sessionContext,
    },
  };

  if (IS_DEV) {
    console.log('[ErrorReporting] Captured message:', report);
  }

  if (!IS_ENABLED) return;

  addToBuffer(report);

  // TODO: Send to actual error service
  // Sentry.captureMessage(message, { level: severity, tags, extra });
}

/**
 * Create error boundary handler
 * @param {string} componentName - Name of the component boundary
 * @returns {Function} Error handler function
 */
export function createErrorHandler(componentName) {
  return (error, errorInfo) => {
    captureError(error, {
      category: ErrorCategory.UI,
      severity: ErrorSeverity.ERROR,
      extra: {
        componentName,
        componentStack: errorInfo?.componentStack,
      },
      tags: ['error-boundary', componentName],
    });
  };
}

/**
 * Capture API error with full context
 * @param {Error} error - Error object
 * @param {Object} requestInfo - Request information
 */
export function captureApiError(error, requestInfo = {}) {
  captureError(error, {
    category: ErrorCategory.API,
    severity: ErrorSeverity.ERROR,
    extra: {
      url: requestInfo.url,
      method: requestInfo.method,
      status: requestInfo.status,
      duration: requestInfo.duration,
    },
    tags: ['api-error', requestInfo.method?.toLowerCase()].filter(Boolean),
  });
}

/**
 * Capture authentication error
 * @param {Error} error - Error object
 * @param {string} action - Auth action (login, logout, refresh, etc.)
 */
export function captureAuthError(error, action) {
  captureError(error, {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.WARNING,
    extra: { action },
    tags: ['auth', action],
  });
}

// ========================================
// INTERNAL UTILITIES
// ========================================

function setupGlobalHandlers() {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    captureError(
      event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason)),
      {
        category: ErrorCategory.UNKNOWN,
        severity: ErrorSeverity.ERROR,
        tags: ['unhandled-rejection'],
      }
    );
  });

  // Global errors
  window.addEventListener('error', (event) => {
    // Ignore errors from browser extensions
    if (event.filename?.includes('chrome-extension://')) return;
    
    captureError(event.error || new Error(event.message), {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.ERROR,
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      tags: ['global-error'],
    });
  });
}

function addToBuffer(report) {
  errorBuffer.push(report);
  
  // Keep buffer size in check
  while (errorBuffer.length > MAX_BUFFER_SIZE) {
    errorBuffer.shift();
  }
}

function sanitizeExtraData(data) {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  // Remove sensitive fields
  const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization'];
  for (const key of Object.keys(sanitized)) {
    if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
      sanitized[key] = '[REDACTED]';
    }
  }
  
  return sanitized;
}

function maskEmail(email) {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const masked = local.length > 2 
    ? local[0] + '***' + local[local.length - 1]
    : '***';
  return `${masked}@${domain}`;
}

// ========================================
// EXPORTS
// ========================================

export default {
  init: initErrorReporting,
  setUser: setUserContext,
  captureError,
  captureMessage,
  captureApiError,
  captureAuthError,
  addBreadcrumb,
  createErrorHandler,
  ErrorSeverity,
  ErrorCategory,
};
